"use client"

import { Fragment, useState } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { InfoModal } from "@/components/ui/info-modal"
import { PersonPackageIcon } from "@/components/ui/package-icons"
import {
  BLOOD_COMPARISON,
  IMAGING_COMPARISON,
} from "@/components/ui/compare-mri-packages"
import { pricingKeys as k } from "@/i18n/pricing-keys"
import { usePricingV5Copy } from "./pricing-v5-copy"

type Row = {
  featureKey: string
  core: boolean
  advanced: boolean
  pro: boolean
  infoKey?: string
}

/** Filled disc for included, a light dash for not, as in the booking app. */
function Mark({ on, label }: { on: boolean; label: string }) {
  return on ? (
    <span className="bg-deep-green-100 text-deep-green-700 inline-flex size-6 items-center justify-center rounded-full">
      <svg aria-hidden viewBox="0 0 24 24" className="size-4">
        <path
          d="M5.5 12.5l4.25 4.25L18.5 8"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  ) : (
    <span className="text-neutral-300" aria-label={label}>
      –
    </span>
  )
}

/**
 * "Compare packages" on the full-body MRI landing: the booking app's popup
 * (ahead-booking-membership-first, MriPackageComparisonModal), one column
 * per package the site sells (Core is back as the plain full-body MRI,
 * 12.10). Same rows and copy as the site's `CompareMriPackages`; a row no
 * package sold here has drops out. The imaging details print under their label, the blood
 * ones are a paragraph, so those open on a tap.
 */
export function MriPlanCompare() {
  const t = useTranslations("pricing") as unknown as (key: string) => string
  const { MRI } = usePricingV5Copy()
  const [open, setOpen] = useState(false)
  const [openInfo, setOpenInfo] = useState<string | null>(null)
  const sections: { heading: string; rows: readonly Row[]; inline: boolean }[] =
    [
      {
        heading: t(k.comparisonImaging),
        rows: IMAGING_COMPARISON,
        inline: true,
      },
      {
        heading: t(k.comparisonBloodAnalysis),
        rows: BLOOD_COMPARISON,
        inline: false,
      },
    ]
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group flex w-fit cursor-pointer items-center gap-2 rounded-full px-4 py-2 transition-colors duration-200 hover:bg-neutral-50"
      >
        <PersonPackageIcon size={22} className="shrink-0 text-neutral-900" />
        <span className="type-body-md font-medium text-neutral-500 underline decoration-neutral-300/55 underline-offset-4 transition-[text-decoration-color] duration-200 group-hover:decoration-neutral-500">
          {t(k.comparePackages)}
        </span>
      </button>
      <InfoModal
        open={open}
        onClose={() => setOpen(false)}
        label={t(k.comparePackages)}
        maxWidth={720}
      >
        <h2 className="type-title-lg text-neutral-900">
          {t(k.comparePackages)}
        </h2>
        <table className="mt-2 w-full border-collapse text-left">
          <colgroup>
            <col />
            {MRI.packages.map((pkg) => (
              <col key={pkg.id} className="w-[84px] md:w-[124px]" />
            ))}
          </colgroup>
          <tbody>
            {sections.map((section, s) => (
              <Fragment key={section.heading}>
                <tr className="border-b border-neutral-200">
                  <th
                    scope="colgroup"
                    className="type-body-sm-emphasized pt-6 pb-2 align-bottom text-neutral-500"
                  >
                    {section.heading}
                  </th>
                  {/* The package names once, level with the first band. */}
                  {MRI.packages.map((pkg) => (
                    <th
                      key={pkg.id}
                      scope="col"
                      className="type-body-md-emphasized pt-6 pb-2 text-center align-bottom text-neutral-900"
                    >
                      {s === 0 ? pkg.name : null}
                    </th>
                  ))}
                </tr>
                {section.rows
                  .filter((row) => MRI.packages.some((pkg) => row[pkg.id]))
                  .map((row, i) => {
                    const info = row.infoKey ? t(row.infoKey) : null
                    const expanded = openInfo === row.featureKey
                    return (
                      <tr
                        key={row.featureKey}
                        className={i % 2 === 1 ? "bg-neutral-50" : undefined}
                      >
                        <td className="py-2.5 pr-3 align-middle">
                          <span className="type-body-md text-neutral-900">
                            {t(row.featureKey)}
                          </span>
                          {info && section.inline && (
                            <span className="type-body-sm mt-0.5 block text-neutral-500">
                              {info}
                            </span>
                          )}
                          {info && !section.inline && (
                            <>
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenInfo(expanded ? null : row.featureKey)
                                }
                                aria-expanded={expanded}
                                className="type-body-sm ml-2 cursor-pointer text-neutral-500 underline underline-offset-2 hover:text-neutral-900"
                              >
                                {t(k.moreInfo)}
                              </button>
                              {expanded && (
                                <span className="type-body-sm mt-1 block text-neutral-500">
                                  {info}
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        {MRI.packages.map((pkg) => (
                          <td
                            key={pkg.id}
                            className="py-2.5 text-center align-middle"
                          >
                            <Mark
                              on={row[pkg.id]}
                              label={row[pkg.id] ? "Included" : "Not included"}
                            />
                          </td>
                        ))}
                      </tr>
                    )
                  })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </InfoModal>
    </>
  )
}
